/**
 * Page Transition Component - CSS animations triggered by route changes
 */

import React from 'react';
import { useLocation } from 'react-router';
import { cn } from '~/lib/utils';

interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
  transition?: 'fade' | 'slide' | 'scale';
  duration?: number;
}

export function PageTransition({ 
  children, 
  className
}: PageTransitionProps) {
  const location = useLocation();

  // Create a transition key that excludes portfolio sub-routes
  // Portfolio navigation between asset types shouldn't trigger page transitions
  const getTransitionKey = (pathname: string) => {
    // For portfolio routes like /portfolio/stocks, /portfolio/crypto, etc.
    // Use just '/portfolio' as the key so switching between asset types doesn't trigger transitions
    if (pathname.startsWith('/portfolio/')) {
      return '/portfolio';
    }
    
    // For all other routes, use the full pathname
    return pathname;
  };

  const transitionKey = getTransitionKey(location.pathname);

  // Use CSS animations with key prop to trigger on route change
  const animationClasses = cn(
    "animate-in fade-in slide-in-from-bottom-2 duration-700",
    className
  );

  return (
    <div key={transitionKey} className={animationClasses}>
      {children}
    </div>
  );
}

/**
 * Route Transition Wrapper - Wraps page content with transitions
 */
interface RouteTransitionProps {
  children: React.ReactNode;
  className?: string;
}

export function RouteTransition({ children, className }: RouteTransitionProps) {
  return (
    <PageTransition className={cn("min-h-full", className)}>
      {children}
    </PageTransition>
  );
}

/**
 * Content Transition - For loading-to-content transitions
 * Triggers animation when content becomes available after loading
 */
interface ContentTransitionProps {
  children: React.ReactNode;
  isLoading: boolean;
  className?: string;
}

export function ContentTransition({ children, isLoading, className }: ContentTransitionProps) {
  // Use a key that changes when loading state changes to trigger re-animation
  const transitionKey = isLoading ? 'loading' : 'content';
  
  const animationClasses = cn(
    "animate-in fade-in slide-in-from-bottom-2 duration-700",
    className
  );

  return (
    <div key={transitionKey} className={animationClasses}>
      {children}
    </div>
  );
}

/**
 * Modal Transition - For bottom sheets and modals
 */
export function ModalTransition({ children, isOpen }: { children: React.ReactNode; isOpen: boolean }) {
  return (
    <div 
      className={cn(
        "transition-all duration-300 ease-out",
        isOpen 
          ? "opacity-100 translate-y-0" 
          : "opacity-0 translate-y-4 pointer-events-none"
      )}
    >
      {children}
    </div>
  );
}